// Multipart uploads with progress (XHR: fetch has no upload progress), with
// the same auth and single 401 retry as the JSON client.
import { api, API_BASE_URL, ApiError } from "./client";
import type { Problem } from "./types";

function send(path: string, form: FormData, onProgress: (fraction: number) => void, signal?: AbortSignal) {
  return new Promise<{ status: number; body: unknown; retryAfter: string | null }>((resolve, reject) => {
    // Aborted before (re)sending, e.g. while the session was refreshed.
    if (signal?.aborted) return reject(new DOMException("aborted", "AbortError"));
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    const settle = <T>(fn: (v: T) => void) => (v: T) => {
      signal?.removeEventListener("abort", abort);
      fn(v);
    };
    const done = settle(resolve);
    const fail = settle(reject);
    xhr.open("POST", `${API_BASE_URL}${path}`);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("Accept-Language", "vi");
    const token = api.current?.access_token;
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let body: unknown = null;
      try { body = JSON.parse(xhr.responseText); } catch { /* non-JSON error page */ }
      done({ status: xhr.status, body, retryAfter: xhr.getResponseHeader("Retry-After") });
    };
    xhr.onerror = () => fail(new ApiError({ status: 0, code: "network_error", detail: "Không kết nối được máy chủ." }));
    xhr.onabort = () => fail(new DOMException("aborted", "AbortError"));
    signal?.addEventListener("abort", abort);
    xhr.send(form);
  });
}

/**
 * POSTs `form` to `path`, reporting the uploaded fraction. Retries once after
 * a token refresh; a non-2xx answer throws its problem as an ApiError (with
 * `details.retry_after` in seconds when the answer has a Retry-After header).
 */
export async function uploadForm<T>(path: string, form: FormData, onProgress: (fraction: number) => void, signal?: AbortSignal): Promise<T> {
  let res = await send(path, form, onProgress, signal);
  if (res.status === 401 && api.current) {
    // Any authed call refreshes the session (single-flight) on 401.
    await api.request("GET", "/v1/admin/me").catch(() => undefined);
    res = await send(path, form, onProgress, signal);
  }
  if (res.status < 200 || res.status >= 300) {
    const problem = (res.body as Problem | null) ?? { status: res.status, code: "upload_failed", detail: "Tải lên thất bại." };
    // A busy server says when to come back (seconds): kept as details.retry_after.
    const wait = Number(res.retryAfter);
    throw new ApiError(res.retryAfter && Number.isFinite(wait) && wait >= 0
      ? { ...problem, details: { ...problem.details, retry_after: Math.ceil(wait) } } : problem);
  }
  return res.body as T;
}
